import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { CareerCardData } from "./CareerCardTypes";

const getYearFromDate = (dateStr: string): string => {
  if (!dateStr) return "";
  if (dateStr === "2999-12-31") return "Present";
  const date = new Date(dateStr);
  return date.toString() !== "Invalid Date" ? date.getFullYear().toString() : dateStr;
};

export const exportCareercardToExcel = async (data: CareerCardData) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Career Card");

  // Column widths
  sheet.columns = [
    { width: 25 },
    { width: 25 },
    { width: 30 },
    { width: 50 },
    { width: 10 },
    { width: 10 },
    { width: 10 },
    { width: 10 }
  ];

  // Common styles
  const headerFill = { 
    type: "pattern" as const, 
    pattern: "solid" as const, 
    fgColor: { argb: "FF4682B4" } // Steel blue color (matches the other implementation)
  };
  
  const dataCellFill = { 
    type: "pattern" as const, 
    pattern: "solid" as const, 
    fgColor: { argb: "FFADD8E6" } // Light blue for data cells
  };
  
  const placeholderFill = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFF2F2F2" } // Light gray for placeholders
  };
  
  const boldFont = { bold: true, size: 12 };
  const headerFont = { bold: true, size: 14, color: { argb: "FFFFFFFF" } }; // White text for headers
  
  const borderThin = {
    top: { style: "thin" as const },
    left: { style: "thin" as const },
    bottom: { style: "thin" as const },
    right: { style: "thin" as const }
  };
  
  const borderMedium = {
    top: { style: "medium" as const },
    left: { style: "medium" as const },
    bottom: { style: "medium" as const },
    right: { style: "medium" as const }
  };

  // === Image placeholders ===
  sheet.mergeCells("A1:A8");
  sheet.mergeCells("B1:B8");

  const logoCell = sheet.getCell("A1");
  logoCell.value = "Employee Photo\n(Image placeholder) "; 
  logoCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  logoCell.border = borderThin;
  logoCell.fill = placeholderFill;
  logoCell.font = { italic: true, size: 12 };

  const photoCell = sheet.getCell("B1");
  photoCell.value = "TTEC Values\n(Image placeholder)";
  photoCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  photoCell.border = borderThin;
  photoCell.fill = placeholderFill;
  photoCell.font = { italic: true, size: 12 };

  // === Personal Information ===
  const personalInfo: [string, string | undefined][] = [
    ["Name:", data.name],
    ["Job Level:", data.jobLevel],
    ["Job Title:", data.jobTitle],
    ["Time at TTEC:", data.timeAtTTEC],
    ["Time in Current Role:", data.time_in_current_role],
    ["Education/ Degree:", data.education_degree],
    ["Certification(s):", data.certificates],
    ["Languages:", data.emp_languages?.map(lang => lang.language_dropdown?.language).join(", ") || ""]
  ];

  personalInfo.forEach((row, i) => {
    const labelCell = sheet.getCell(`C${i + 1}`);
    labelCell.value = row[0];
    labelCell.font = boldFont;
    labelCell.border = borderThin;
    labelCell.fill = dataCellFill;
    
    // Merge columns D and E for the values
    sheet.mergeCells(`D${i + 1}:E${i + 1}`);
    
    const valueCell = sheet.getCell(`D${i + 1}`);
    valueCell.value = row[1] || "";
    valueCell.border = borderThin;
    valueCell.fill = dataCellFill;
    valueCell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    
    // Set row height for personal information
    sheet.getRow(i + 1).height = 20;
  });

  // === Section: Internal Mobility ===
  sheet.mergeCells("A9:E9");
  const mobilityHeader = sheet.getCell("A9");
  mobilityHeader.value = "Location Mobility";
  mobilityHeader.font = headerFont;
  mobilityHeader.fill = headerFill;
  mobilityHeader.border = borderMedium;
  mobilityHeader.alignment = { vertical: "middle", horizontal: "left" };

  const mobilityData: [string, string | undefined][] = [
    ["Current Location:", data.location_desc],
    ["Willingness to Relocate:", data.willingness_to_relocate],
    ["Location Preferences:", data.EmpLocationPreferences?.map(loc => loc.location_dropdown?.location).join(", ") || ""]
  ];

  mobilityData.forEach((row, i) => {
    // Merge columns A and B for the label
    sheet.mergeCells(`A${10 + i}:B${10 + i}`);
    
    const label = sheet.getCell(`A${10 + i}`);
    label.value = row[0];
    label.font = boldFont;
    label.border = borderThin;
    label.fill = dataCellFill;
    label.alignment = { vertical: "middle", horizontal: "left" };
    
    // Set row height for mobility information
    sheet.getRow(10 + i).height = 25;

    // Use columns C to E for the values (merged)
    sheet.mergeCells(`C${10 + i}:E${10 + i}`);
    const value = sheet.getCell(`C${10 + i}`);
    value.value = row[1] || "";
    value.border = borderThin;
    value.fill = dataCellFill;
    value.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  });

  // === Section: Role Readiness ===
  sheet.mergeCells("A13:E13");
  const roleHeader = sheet.getCell("A13");
  roleHeader.value = "Role Aspirations";
  roleHeader.font = headerFont;
  roleHeader.fill = headerFill;
  roleHeader.border = borderMedium;
  roleHeader.alignment = { vertical: "middle", horizontal: "left" };

  const roleData: [string, string | undefined][] = [
    ["Your next role preference:", data.next_role_preference || ""],
    ["Future career aspirations:", data.future_career_aspirations],
    ["Your ideal timing for next role:", data.timing_for_next_role]
  ];

  roleData.forEach((row, i) => {
    // Merge columns A and B for the label
    sheet.mergeCells(`A${14 + i}:B${14 + i}`);
    
    const label = sheet.getCell(`A${14 + i}`);
    label.value = row[0];
    label.font = boldFont;
    label.border = borderThin;
    label.fill = dataCellFill;
    label.alignment = { vertical: "middle", horizontal: "left" };

    // Use columns C to E for the values (merged)
    sheet.mergeCells(`C${14 + i}:E${14 + i}`);
    const value = sheet.getCell(`C${14 + i}`);
    value.value = row[1] || "";
    value.border = borderThin;
    value.fill = dataCellFill;
    value.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    
    // Set row height for role aspirations
    sheet.getRow(14 + i).height = 25;
  });
  
  // === Section: Work Experience ===
  sheet.mergeCells("A17:E17");
  const workExpHeader = sheet.getCell("A17");
  workExpHeader.value = "Experience (TTEC and External)";
  workExpHeader.font = headerFont;
  workExpHeader.fill = headerFill;
  workExpHeader.border = borderMedium;
  workExpHeader.alignment = { vertical: "middle", horizontal: "left" };
  
      // Only process work experience if it exists
  if (data.work_experiences && data.work_experiences.length > 0) {
    // Only show up to 4 work experiences
    const experiencesToShow = data.work_experiences.slice(0, 4);
    
    experiencesToShow.forEach((exp: {
      company_name: string;
      previous_position_role: string;
      starting_date: string;
      end_date: string;
      current_position: boolean;
      short_description?: string;
    }, i: number) => {
      // Each experience takes just one row now
      const rowIndex = 18 + i;
      
      // Column A: Role label
      const label = sheet.getCell(`A${rowIndex}`);
      label.value = "Previous Position";
      label.font = boldFont;
      label.border = borderThin;
      label.fill = dataCellFill;
      label.alignment = { vertical: "middle", horizontal: "left" };

      // Column B: Position Title
      const position = exp.company_name === "TTEC" ? 
        `TTEC - ${exp.previous_position_role}` :
        `${exp.company_name} - ${exp.previous_position_role}`;
      
      const value = sheet.getCell(`B${rowIndex}`);
      value.value = position;
      value.border = borderThin;
      value.fill = dataCellFill;
      value.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      
      // Column C: Date Range (Year-only format)
      const startYear = getYearFromDate(exp.starting_date);
      const endYear = exp.current_position ? "Present" : getYearFromDate(exp.end_date);
      const dateRange = `${startYear}${startYear && endYear ? ' - ' : ''}${endYear}`;
      const dateCell = sheet.getCell(`C${rowIndex}`);
      dateCell.value = dateRange;
      dateCell.border = borderThin;
      dateCell.fill = dataCellFill;
      dateCell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      
      // Column D-E: Description (merged)
      sheet.mergeCells(`D${rowIndex}:E${rowIndex}`);
      const descValue = sheet.getCell(`D${rowIndex}`);
      descValue.value = exp.short_description || "";
      descValue.border = borderThin;
      descValue.fill = dataCellFill;
      descValue.alignment = { vertical: "middle", horizontal: "left", wrapText: true };      // Set row height for work experience
      sheet.getRow(18 + i).height = 25;
    });
    
    // Fill in any remaining rows with empty cells but maintain the light blue background
    // This ensures we always have 4 rows with the light blue background
    const actualExperienceCount = experiencesToShow.length;
    for (let i = actualExperienceCount; i < 4; i++) {
      const rowIndex = 18 + i;
      
      // Column A
      const emptyLabel = sheet.getCell(`A${rowIndex}`);
      emptyLabel.value = "";  // Empty value
      emptyLabel.border = borderThin;
      emptyLabel.fill = dataCellFill;  // Light blue background
      
      // Column B
      const emptyPosition = sheet.getCell(`B${rowIndex}`);
      emptyPosition.value = "";  // Empty value
      emptyPosition.border = borderThin;
      emptyPosition.fill = dataCellFill;  // Light blue background
      
      // Column C
      const emptyDate = sheet.getCell(`C${rowIndex}`);
      emptyDate.value = "";  // Empty value
      emptyDate.border = borderThin;
      emptyDate.fill = dataCellFill;  // Light blue background
      
      // Columns D-E (merged)
      sheet.mergeCells(`D${rowIndex}:E${rowIndex}`);
      const emptyDesc = sheet.getCell(`D${rowIndex}`);
      emptyDesc.value = "";  // Empty value
      emptyDesc.border = borderThin;
      emptyDesc.fill = dataCellFill;  // Light blue background
      
      // Set row height for empty rows
      sheet.getRow(rowIndex).height = 25;
    }
  }
  
  // === Section: Additional TTEC Leadership Roles ===
  // We now have 4 rows for experience (4 positions × 1 row each), so start at row 22
  sheet.mergeCells("A22:E22");
  const additionalRolesHeader = sheet.getCell("A22");
  additionalRolesHeader.value = "Additional TTEC Leadership Roles";
  additionalRolesHeader.font = headerFont;
  additionalRolesHeader.fill = headerFill;
  additionalRolesHeader.border = borderMedium;
  additionalRolesHeader.alignment = { vertical: "middle", horizontal: "left" };

  // Employee Resource Groups (ERGs)
  sheet.mergeCells("A23:B23");
  sheet.getCell("A23").value = "Employee Resource Groups (ERGs):";
  sheet.getCell("A23").font = boldFont;
  sheet.getCell("A23").border = borderThin;
  sheet.getCell("A23").fill = dataCellFill;
  sheet.getCell("A23").alignment = { vertical: "middle", horizontal: "left" };
  
  // Value cell for ERGs - get from data if available
  const ergsValue = data.EmpResourceGroup?.map(item => 
    item.resource_group_dropdown?.resource_group || ""
  ).join(", ") || "";
    
  // Merge columns C to E for ERGs value
  sheet.mergeCells("C23:E23");
  sheet.getCell("C23").value = ergsValue;
  sheet.getCell("C23").border = borderThin;
  sheet.getCell("C23").fill = dataCellFill;
  sheet.getCell("C23").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(23).height = 25; // Set appropriate height for ERGs row
  
  // TTEC Communities
  sheet.mergeCells("A24:B24");
  sheet.getCell("A24").value = "TTEC Communities:";
  sheet.getCell("A24").font = boldFont;
  sheet.getCell("A24").border = borderThin;
  sheet.getCell("A24").fill = dataCellFill;
  sheet.getCell("A24").alignment = { vertical: "middle", horizontal: "left" };
  
  // Value cell for TTEC Communities
  const communitiesValue = data.EmpTtecCommunities?.map(item => 
    item.ttec_communities || ""
  ).join(", ") || "";
    
  // Merge columns C to E for Communities value
  sheet.mergeCells("C24:E24");
  sheet.getCell("C24").value = communitiesValue;
  sheet.getCell("C24").border = borderThin;
  sheet.getCell("C24").fill = dataCellFill;
  sheet.getCell("C24").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(24).height = 25; // Set appropriate height for Communities row

  // === Section: Leadership Culture ===
  sheet.mergeCells("A25:E25");
  const leadershipHeader = sheet.getCell("A25");
  leadershipHeader.value = "Leadership Culture";
  leadershipHeader.font = headerFont;
  leadershipHeader.fill = headerFill;
  leadershipHeader.border = borderMedium;
  leadershipHeader.alignment = { vertical: "middle", horizontal: "left" };

  // Strengths displayed
  sheet.mergeCells("A26:B26");
  sheet.getCell("A26").value = "Strengths displayed:";
  sheet.getCell("A26").font = boldFont;
  sheet.getCell("A26").border = borderThin;
  sheet.getCell("A26").fill = dataCellFill;
  sheet.getCell("A26").alignment = { vertical: "middle", horizontal: "left" };
  
  // Value cell for strengths - get strengths from data if available
  const strengthsValue = data.EmpLeadershipCulture?.filter(item => item.flag === "STRENGTH")
    .map(item => item.leadership_culture_dropdown_sd?.name || "")
    .join(", ") || "";
    
  // Merge columns C to E for strengths value
  sheet.mergeCells("C26:E26");
  sheet.getCell("C26").value = strengthsValue;
  sheet.getCell("C26").border = borderThin;
  sheet.getCell("C26").fill = dataCellFill;
  sheet.getCell("C26").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(26).height = 30; // Set appropriate height for strengths row
  
  // Areas of development
  sheet.mergeCells("A27:B27");
  sheet.getCell("A27").value = "Areas of development:";
  sheet.getCell("A27").font = boldFont;
  sheet.getCell("A27").border = borderThin;
  sheet.getCell("A27").fill = dataCellFill;
  sheet.getCell("A27").alignment = { vertical: "middle", horizontal: "left" };
  
  // Value cell for areas of development
  const developmentValue = data.EmpLeadershipCulture?.filter(item => item.flag === "DEVELOPMENT")
    .map(item => item.leadership_culture_dropdown_sd?.name || "")
    .join(", ") || "";
    
  // Merge columns C to E for development value
  sheet.mergeCells("C27:E27");
  sheet.getCell("C27").value = developmentValue;
  sheet.getCell("C27").border = borderThin;
  sheet.getCell("C27").fill = dataCellFill;
  sheet.getCell("C27").alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(27).height = 30; // Set appropriate height for development areas row
  
  // === Section: Performance Summary ===
  sheet.mergeCells("A28:E28");
  const perfSummaryHeader = sheet.getCell("A28");
  perfSummaryHeader.value = "Performance Summary";
  perfSummaryHeader.font = headerFont;
  perfSummaryHeader.fill = headerFill;
  perfSummaryHeader.border = borderMedium;
  perfSummaryHeader.alignment = { vertical: "middle", horizontal: "left" };

  // Top Accomplishments description
  sheet.mergeCells("A29:E29");
  const accomplishmentsDesc = sheet.getCell("A29");
  accomplishmentsDesc.value = "What were your top 3-5 key deliverables/accomplishments for the previous performance year (personal development or business deliverable)?";
  accomplishmentsDesc.font = { bold: true, size: 10 };
  accomplishmentsDesc.border = borderThin;
  accomplishmentsDesc.fill = dataCellFill;
  accomplishmentsDesc.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(29).height = 30; // Set appropriate height for question row
  
  // Top Accomplishments value
  sheet.mergeCells("A30:E30");
  const accomplishmentsValue = sheet.getCell("A30");
  accomplishmentsValue.value = data.last_year_top_achievement || "";
  accomplishmentsValue.border = borderThin;
  accomplishmentsValue.fill = dataCellFill;
  accomplishmentsValue.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(30).height = 70; // Set taller height for answer row to show more content
  
  // What would you have done better description
  sheet.mergeCells("A31:E31");
  const betterDesc = sheet.getCell("A31");
  betterDesc.value = "What would you have liked to have done better during the previous performance year (personal development or business deliverable)? Provide an example where you didn't achieve the result you were hoping for. How did you overcome it/ what did you learn from the experience?";
  betterDesc.font = { bold: true, size: 10 };
  betterDesc.border = borderThin;
  betterDesc.fill = dataCellFill;
  betterDesc.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(31).height = 45; // Set appropriate height for this longer question row
  
  // What would you have done better value
  sheet.mergeCells("A32:E32");
  const betterValue = sheet.getCell("A32");
  betterValue.value = data.last_year_personal_development_review || "";
  betterValue.border = borderThin;
  betterValue.fill = dataCellFill;
  betterValue.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(32).height = 70; // Set taller height for answer row to show more content
  
  // Redo for prior business deliverable question
  sheet.mergeCells("A33:E33");
  const redoDesc = sheet.getCell("A33");
  redoDesc.value = "If you had a redo for a prior business deliverable, what would it be?";
  redoDesc.font = { bold: true, size: 10 };
  redoDesc.border = borderThin;
  redoDesc.fill = dataCellFill;
  redoDesc.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(33).height = 30; // Set appropriate height for question row
  
  // Redo value
  sheet.mergeCells("A34:E34");
  const redoValue = sheet.getCell("A34");
  redoValue.value = data.redo_for_previous_business_year || "";
  redoValue.border = borderThin;
  redoValue.fill = dataCellFill;
  redoValue.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(34).height = 70; // Set taller height for answer row to show more content
  
  // Goals for next two quarters description
  sheet.mergeCells("A35:E35");
  const goalsDesc = sheet.getCell("A35");
  goalsDesc.value = "What are your goals or focus areas for the next two quarters? What areas of development are you focused on and your major milestones ahead of you?";
  goalsDesc.font = { bold: true, size: 10 };
  goalsDesc.border = borderThin;
  goalsDesc.fill = dataCellFill;
  goalsDesc.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(35).height = 40; // Set appropriate height for this question row
  
  // Goals for next two quarters value
  sheet.mergeCells("A36:E36");
  const goalsValue = sheet.getCell("A36");
  goalsValue.value = data.goal_next_two_quarters || "";
  goalsValue.border = borderThin;
  goalsValue.fill = dataCellFill;
  goalsValue.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  sheet.getRow(36).height = 70; // Set taller height for answer row to show more content
  goalsValue.fill = dataCellFill;
  goalsValue.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  
  // No duplicate work experience section - already moved above
  
  // === Save the file ===
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `Career_Card_${data.name}.xlsx`);
};
